import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo } from "react";
import { Control, UseFieldArrayAppend, UseFieldArrayRemove } from "react-hook-form";

import { PRICE_DIMENSION, PRICE_DIMENSION_LABEL } from "@/features/organization/product/constants/productEnums";
import { TPriceCombinationForm, TProductForm, TTypePriceForm } from "@/api/services/main/organization/product/dtos/product.dto";
import { emptyCombinationForm } from "@/features/organization/product/utils/productForm";
import { usesOtherDimension } from "@/features/organization/product/utils/productUtils";
import { useResetOrphanedCombinations } from "@/features/organization/product/hooks/useResetOrphanedCombinations";
import { buildCombinationColumns } from "@/features/organization/product/components/product-form/product-combination/combinationColumns";
import { TOption } from "@/types/common";

export type TUseProductCombinationSectionParams = {
  control: Control<TProductForm>;
  productName: string;
  isTierDiscountTarget: boolean;
  typePrices: TTypePriceForm[];
  rows: TPriceCombinationForm[];
  nationalityEnabled: boolean;
  fieldEnabled: boolean;
  variantEnabled: boolean;
  append: UseFieldArrayAppend<TProductForm, "combinations">;
  remove: UseFieldArrayRemove;
  /** id → label resolvers (from useProductMasters) for the option labels. */
  getNationalityName: (id: number | string | null | undefined) => string;
  getIndustryName: (id: number | string | null | undefined) => string;
};

export const useProductCombinationSection = ({
  control,
  productName,
  isTierDiscountTarget,
  typePrices,
  rows,
  nationalityEnabled,
  fieldEnabled,
  variantEnabled,
  append,
  remove,
  getNationalityName,
  getIndustryName,
}: TUseProductCombinationSectionParams): {
  showThirdDimension: boolean;
  thirdDimensionHeader: string;
  columns: MRT_ColumnDef<TPriceCombinationForm>[];
} => {
  // Always keep at least one (empty) row, even after delete-all.
  useEffect(() => {
    if (rows.length === 0) append(emptyCombinationForm());
  }, [rows.length, append]);

  const isOtherDimension = usesOtherDimension(productName);
  const showThirdDimension = isOtherDimension || isTierDiscountTarget;
  const thirdDimension = isOtherDimension ? PRICE_DIMENSION.OTHER : PRICE_DIMENSION.TIER;
  const thirdDimensionHeader = PRICE_DIMENSION_LABEL[thirdDimension];

  // Drop dangling combination references when a 種別単価 row (nationality / field / tier / other) is deleted.
  useResetOrphanedCombinations({ typePrices, thirdDimension });

  const { nationalityOptions, fieldOptions, thirdDimensionOptions } = useMemo(() => {
    const nationalityOptions: TOption[] = [];
    const fieldOptions: TOption[] = [];
    const thirdDimensionOptions: TOption[] = [];

    const seenNationality = new Set<string>();
    const seenField = new Set<string>();

    for (const typePrice of typePrices) {
      if (typePrice.isDefault) continue;

      if (nationalityEnabled && typePrice.dimension === PRICE_DIMENSION.NATIONALITY) {
        const value = typePrice.nationalityId;
        if (value && !seenNationality.has(value)) {
          seenNationality.add(value);
          nationalityOptions.push({ value, label: getNationalityName(value) });
        }
      }

      if (fieldEnabled && typePrice.dimension === PRICE_DIMENSION.FIELD) {
        const value = typePrice.fieldId;
        if (value && !seenField.has(value)) {
          seenField.add(value);
          fieldOptions.push({ value, label: getIndustryName(value) });
        }
      }

      // Third dimension (ティア / その他): option value = the tier row's id (stable reference), label =
      // its current text. Editing the tier text re-renders the label, so combination display updates
      // live. Ids are unique, so no dedupe is needed; skip rows with no text yet.
      if (showThirdDimension && typePrice.dimension === thirdDimension) {
        const label = typePrice.variantValue?.trim();
        if (label) thirdDimensionOptions.push({ value: typePrice.id, label });
      }
    }

    return {
      nationalityOptions,
      fieldOptions,
      thirdDimensionOptions,
    };
  }, [
    typePrices,
    nationalityEnabled,
    fieldEnabled,
    showThirdDimension,
    thirdDimension,
    getNationalityName,
    getIndustryName,
  ]);

  const enabledDimensions = useMemo(
    () => ({ nationality: nationalityEnabled, field: fieldEnabled, tier: variantEnabled }),
    [nationalityEnabled, fieldEnabled, variantEnabled],
  );

  const columns = useMemo(
    () =>
      buildCombinationColumns({
        control,
        typePrices,
        nationalityOptions,
        fieldOptions,
        thirdDimensionOptions,
        showThirdDimension,
        thirdDimensionHeader,
        thirdDimension,
        enabledDimensions,
        remove,
      }),
    [
      control,
      typePrices,
      nationalityOptions,
      fieldOptions,
      thirdDimensionOptions,
      showThirdDimension,
      thirdDimensionHeader,
      thirdDimension,
      enabledDimensions,
      remove,
    ],
  );

  return {
    showThirdDimension,
    thirdDimensionHeader,
    columns,
  };
};
